import React, { useState, useEffect } from 'react';
import { getHelpTip } from '../config/helpMessages';
import '../styles/components/contextHelpTip.css';

interface ContextHelpTipProps {
  /** ID of the help tip from helpMessages.ts */
  tipId: string;
  /** Position of the tip */
  position?: 'top' | 'bottom';
  /** Bottom offset in pixels (useful when there's a floating menu) */
  bottomOffset?: number;
  /** Top offset in pixels */
  topOffset?: number;
  /** Whether to show the tip (external control, e.g., only show in certain modes) */
  show?: boolean;
  /** Callback when tip is dismissed */
  onDismiss?: () => void;
}

/**
 * A floating, dismissible help tip that remembers user preference.
 * Shows once per tipId until dismissed, then remembers in localStorage.
 */
export const ContextHelpTip: React.FC<ContextHelpTipProps> = ({
  tipId,
  position = 'bottom',
  bottomOffset = 130,
  topOffset = 200,
  show = true,
  onDismiss,
}) => {
  const [isDismissed, setIsDismissed] = useState(true);
  const [isAnimatingOut, setIsAnimatingOut] = useState(false);

  const tip = getHelpTip(tipId);

  useEffect(() => {
    const dismissed = localStorage.getItem(`help-tip-dismissed-${tipId}`);
    setIsDismissed(dismissed === 'true');
  }, [tipId]);

  const handleDismiss = () => {
    setIsAnimatingOut(true);
    setTimeout(() => {
      localStorage.setItem(`help-tip-dismissed-${tipId}`, 'true');
      setIsDismissed(true);
      setIsAnimatingOut(false);
      onDismiss?.();
    }, 200);
  };

  if (!tip || isDismissed || !show) {
    return null;
  }

  const positionStyle = position === 'bottom'
    ? { bottom: `calc(env(safe-area-inset-bottom, 0px) + ${bottomOffset}px)` }
    : { top: `${topOffset}px` };

  return (
    <div
      className={`context-help-tip ${isAnimatingOut ? 'animating-out' : ''}`}
      style={positionStyle}
    >
      <span className="context-help-tip-icon">{tip.icon}</span>
      <span className="context-help-tip-message">{tip.message}</span>
      <button
        className="context-help-tip-dismiss"
        onClick={handleDismiss}
        aria-label="Dismiss help tip"
      >
        ×
      </button>
    </div>
  );
};

export default ContextHelpTip;
